import { apiClient } from '@/api/api';
import { setErpDefinitions, erpDefinitionsLoaded } from './store';
import { deliverErpDefinitionTexts } from './texts';
import { definitionsCatalogPath } from './paths';
import type { ErpDefinitionsCatalog } from './types';

export { setErpDefinitionTextSink } from './texts';
export type { ErpDefinitionTextSink } from './texts';

/** How long one attempt may take before it is given up (the next call tries again). */
export const ERP_DEFINITIONS_TIMEOUT_MS = 20_000;

// One request, like every other client: the catalog carries the forms, the route
// table, the menu and the texts of both languages. A stalled request must not keep
// the single flight pending forever, so every attempt has a deadline: past it the
// outstanding request is aborted and the load rejects.
async function fetchDefinitions(): Promise<void> {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      controller.abort();
      reject(new Error(`ERP definitions request timed out after ${ERP_DEFINITIONS_TIMEOUT_MS} ms`));
    }, ERP_DEFINITIONS_TIMEOUT_MS);
  });
  try {
    const { data } = await Promise.race([
      apiClient.get<ErpDefinitionsCatalog>(definitionsCatalogPath({ lang: ['de', 'en'] }), {
        signal: controller.signal,
      }),
      deadline,
    ]);
    setErpDefinitions({
      version: data.version,
      forms: data.forms,
      routes: data.routes,
      menu: data.menu,
      texts: { de: data.texts.de, en: data.texts.en },
    });
    deliverErpDefinitionTexts();
  } finally {
    clearTimeout(timer);
  }
}

let inFlight: Promise<void> | null = null;

/**
 * Loads the ERP's screen definitions (forms, route table, menu, both languages
 * of the texts) over the API in one request (`GET /definitions`). Single flight: concurrent callers share one load,
 * later calls after a success resolve at once, and a failed load is retried by
 * the next call.
 */
export function loadErpDefinitions(): Promise<void> {
  if (erpDefinitionsLoaded.value) return Promise.resolve();
  if (!inFlight) {
    inFlight = fetchDefinitions().finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
}
